import { useState } from 'react';
import { Building2, Globe, Layers, Loader2, MapPin, Package, Pencil, Plus, ShoppingBag, ShoppingCart, Store, Trash2, X } from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { useChannels, KIND_LABELS, type Channel } from '@/hooks/useChannels';
import { useActiveChannel } from '@/store/activeChannel';
import { validateName } from '@/lib/validators';
import { cn } from '@/lib/utils';

type ChannelKind = Channel['kind'];

const KIND_ICONS: Record<ChannelKind, React.ComponentType<{ className?: string }>> = {
  mercado_livre: ShoppingBag,
  shopee:        ShoppingCart,
  shopify:       Store,
  site_proprio:  Globe,
  atacado:       Building2,
  loja_fisica:   MapPin,
  amazon:        Package,
  outro:         Layers,
};

const COLORS = ['#0f3d2e','#ffe600','#ee4d2d','#96bf48','#3b82f6','#8b5cf6','#f59e0b','#ff9900','#64748b'];

function ChannelForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: Partial<Channel>;
  onSave: (name: string, kind: ChannelKind, color: string) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName]     = useState(initial?.name ?? '');
  const [kind, setKind]     = useState<ChannelKind>(initial?.kind ?? 'outro');
  const [color, setColor]   = useState(initial?.color ?? '#0f3d2e');
  const [nameErr, setNameErr] = useState('');

  function handleSave() {
    const err = validateName(name.trim(), { label: 'Nome do canal', min: 2, max: 80 });
    if (err) { setNameErr(err); return; }
    setNameErr('');
    onSave(name.trim(), kind, color);
  }

  return (
    <div className="card p-5 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-ink-muted mb-1">Nome do canal *</label>
          <input
            className={cn('input w-full', nameErr && 'border-negative focus:border-negative')}
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameErr) setNameErr(''); }}
            placeholder="ex: Mercado Livre Principal"
            maxLength={80}
          />
          {nameErr && <p className="text-xs text-negative mt-1">{nameErr}</p>}
        </div>
        <div>
          <label className="block text-xs text-ink-muted mb-1">Tipo *</label>
          <select className="input w-full" value={kind} onChange={(e) => setKind(e.target.value as ChannelKind)}>
            {(Object.entries(KIND_LABELS) as [ChannelKind, string][]).map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs text-ink-muted mb-2">Cor</label>
        <div className="flex gap-2 flex-wrap">
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={cn('w-7 h-7 rounded-lg border-2 transition-all', color === c ? 'border-ink scale-110' : 'border-transparent')}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </div>
      <div className="flex gap-3 justify-end">
        <button onClick={onCancel} className="btn-secondary"><X className="w-4 h-4" />Cancelar</button>
        <button onClick={handleSave} disabled={saving || !name.trim()} className="btn-primary gap-2">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Salvar
        </button>
      </div>
    </div>
  );
}

export function ManageChannels() {
  const { channels, loading, fetchError, createChannel, updateChannel, deleteChannel, setDefaultChannel } = useChannels();
  const { activeChannelId, setActiveChannel } = useActiveChannel();

  const [creating, setCreating]     = useState(false);
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');

  function showSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  }

  async function handleCreate(name: string, kind: ChannelKind, color: string) {
    setSaving(true);
    try { await createChannel(name, kind, color); setCreating(false); showSuccess('Canal criado com sucesso.'); }
    catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleUpdate(id: string, name: string, kind: ChannelKind, color: string) {
    setSaving(true);
    try { await updateChannel(id, { name, kind, color }); setEditingId(null); showSuccess('Canal atualizado.'); }
    catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id: string) {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setConfirmDeleteId(null);
    try {
      await deleteChannel(id);
      if (activeChannelId === id) setActiveChannel(null);
      showSuccess('Canal excluído.');
    } catch (e: any) { setError(e.message); }
  }

  async function handleSetDefault(id: string) {
    try { await setDefaultChannel(id); showSuccess('Canal padrão atualizado.'); }
    catch (e: any) { setError(e.message); }
  }

  const displayError = error || fetchError || '';

  return (
    <PageShell title="Gerenciar canais" subtitle="Organize suas lojas e fontes de receita por canal." back="/configuracoes">
      <div className="space-y-4">
        {displayError && (
          <div className="flex items-start justify-between gap-3 px-4 py-3 rounded-xl border border-negative/30 bg-negative/5 text-sm text-negative">
            <span>{displayError}</span>
            <button onClick={() => setError('')} className="shrink-0 text-negative/60 hover:text-negative">✕</button>
          </div>
        )}
        {success && (
          <div className="px-4 py-3 rounded-xl border border-positive/30 bg-positive/5 text-sm text-positive font-medium">
            ✓ {success}
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={() => setCreating(true)} className="btn-primary gap-2" disabled={creating}>
            <Plus className="w-4 h-4" />Novo canal
          </button>
        </div>

        {creating && (
          <ChannelForm
            onSave={handleCreate}
            onCancel={() => setCreating(false)}
            saving={saving}
          />
        )}

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map((i) => <div key={i} className="h-16 rounded-2xl bg-surface-subtle animate-pulse" />)}
          </div>
        ) : channels.length === 0 ? (
          <div className="card p-8">
            <EmptyState icon={Layers} title="Nenhum canal" description="Crie seu primeiro canal para organizar as operações." />
          </div>
        ) : (
          <div className="space-y-3">
            {channels.map((c) => {
              const Icon = KIND_ICONS[c.kind];
              const isActive = activeChannelId === c.id;

              if (editingId === c.id) {
                return (
                  <ChannelForm
                    key={c.id}
                    initial={c}
                    onSave={(name, kind, color) => handleUpdate(c.id, name, kind, color)}
                    onCancel={() => setEditingId(null)}
                    saving={saving}
                  />
                );
              }

              return (
                <div key={c.id} className={cn('card p-4 flex items-center gap-4', isActive && 'ring-2 ring-brand-500')}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: c.color + '20' }}>
                    <span style={{ color: c.color }} className="flex"><Icon className="w-5 h-5" /></span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink">{c.name}</p>
                    <p className="text-xs text-ink-muted">{KIND_LABELS[c.kind]}{c.is_default ? ' · Canal padrão' : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!isActive && (
                      <button onClick={() => setActiveChannel(c.id)} className="btn-secondary !py-1 !text-xs">
                        Ativar
                      </button>
                    )}
                    {isActive && <span className="chip-positive text-xs">Ativo</span>}
                    {!c.is_default && (
                      <button onClick={() => void handleSetDefault(c.id)} className="btn-secondary !py-1 !text-xs">
                        Padrão
                      </button>
                    )}
                    <button onClick={() => setEditingId(c.id)} className="btn-ghost !p-1.5">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {!c.is_default && confirmDeleteId === c.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDelete(c.id)} className="btn-secondary !py-1 !px-2 !text-xs text-negative border-negative/30 hover:bg-negative/10">
                          Apagar
                        </button>
                        <button onClick={() => setConfirmDeleteId(null)} className="btn-ghost !p-1.5">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : !c.is_default ? (
                      <button onClick={() => handleDelete(c.id)} className="btn-ghost !p-1.5 text-negative hover:text-negative">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageShell>
  );
}
